import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, Card, cn } from "@/core/ui";
import { useSession } from "@/core/auth/useSession";
import { myStanding, useMyAttempts, type AttemptView } from "@/core/play/api";
import { formatDuration } from "@/core/play/time";
import { usePublishedPuzzles, type PuzzleSummary } from "./api";
import { ActivityFeed } from "./ActivityFeed";

export function DifficultyStars({ value, className }: { value: number; className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      aria-label={t("puzzles.difficulty", { value })}
      className={cn("bt-difficulty", className)}
    >
      {"★".repeat(value)}
      <span className="bt-difficulty__empty">{"★".repeat(Math.max(0, 5 - value))}</span>
    </span>
  );
}

/**
 * The caller's own standing on a puzzle, in the last column of its row: a check and the
 * time (or the points, for a `scored` type) once it is solved, a quiet "started" while a
 * run is open. Nothing at all on a puzzle never revealed, so a scan down the column answers
 * "which ones have I done?" without reading a word.
 */
function RowResult({ puzzle, run }: { puzzle: PuzzleSummary; run: AttemptView | null }) {
  const { t } = useTranslation();
  if (!run) return <span className="bt-puzzle-row__result" />;
  const solved = run.status === "solved";
  const scored = solved && puzzle.capabilities.scored && run.score !== null;
  const key = `puzzles.result_${solved ? "solved" : "started"}${scored ? "_scored" : ""}${run.team ? "_team" : ""}`;
  const label = t(key, {
    time: formatDuration(run.elapsed_ms ?? 0),
    count: run.score ?? 0,
    team: run.team?.name,
  });
  return (
    <span
      className={cn("bt-puzzle-row__result", solved && "bt-puzzle-row__result--solved")}
      aria-label={label}
      title={run.team ? label : undefined}
    >
      {solved ? (
        <>
          <svg
            className="bt-puzzle-row__result-mark"
            viewBox="0 0 12 12"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M2 6.5 4.8 9.2 10 3.2"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {scored
            ? t("puzzles.result_points", { count: run.score ?? 0 })
            : formatDuration(run.elapsed_ms ?? 0)}
        </>
      ) : (
        t("puzzles.result_started")
      )}
    </span>
  );
}

const TABS = ["published", "archived"] as const;
type Tab = (typeof TABS)[number];

export function PuzzleListPage() {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const [tab, setTab] = useState<Tab>("published");
  const puzzles = usePublishedPuzzles(tab);
  const attempts = useMyAttempts({ enabled: session.data?.authenticated === true });
  if (!session.data?.authenticated) {
    return (
      <Card>
        <p>{t("puzzles.login_to_play")}</p>
        <Link to="/auth/login" className="bt-puzzles__login-link">
          {t("nav.login")}
        </Link>
      </Card>
    );
  }
  const items = puzzles.data?.results ?? [];
  return (
    <section className="bt-puzzles">
      <div className="bt-puzzles__index">
        <h1 className="bt-page-title">{t("nav.puzzles")}</h1>
        <div role="tablist" aria-label={t("puzzles.filter")} className="bt-puzzle-filter">
          {TABS.map((item) => (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={tab === item}
              onClick={() => setTab(item)}
              className="bt-puzzle-filter__tab"
            >
              {t(`puzzles.tab_${item}`)}
            </button>
          ))}
        </div>
        {tab === "archived" ? (
          <p className="bt-page-subtitle">{t("puzzles.archive_hint")}</p>
        ) : null}
        {puzzles.isPending ? <p className="bt-page-message">{t("app.loading")}</p> : null}
        {!puzzles.isPending && items.length === 0 ? (
          <p className="bt-page-message">
            {t(tab === "archived" ? "puzzles.empty_archive" : "puzzles.empty")}
          </p>
        ) : null}
        <ul className="bt-puzzle-index">
          {items.map((p) => (
            <li key={p.slug} className="bt-puzzle-index__row">
              <Link to={`/puzzles/${p.slug}`} className="bt-puzzle-row">
                <h2 className="bt-puzzle-row__title">{p.title}</h2>
                <p className="bt-puzzle-row__author">{t("puzzles.by", { author: p.author })}</p>
                <DifficultyStars value={p.difficulty} className="bt-puzzle-row__rating" />
                <div className="bt-puzzle-row__meta">
                  <Badge tone="info">
                    {t(`puzzle_types.${p.puzzle_type}`, { defaultValue: p.puzzle_type })}
                  </Badge>
                  {p.published_at ? (
                    <time dateTime={p.published_at}>
                      {new Date(p.published_at).toLocaleDateString(i18n.language)}
                    </time>
                  ) : null}
                </div>
                <RowResult puzzle={p} run={myStanding(attempts.data?.[p.slug])} />
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <ActivityFeed />
    </section>
  );
}
